import * as React from "react";
import Cell from "./Cell";
import Row from "./Row";
import XLabels from "./XLabels";
import Column from "./Column";
import YLabels from "./YLabels";
import YLabelAligner from "./YLabelAligner";
import useElementHeight from "./useElementHeight";
import { Box } from "@mui/material";

export interface HeatMapGridProps {
  data: { item: any; present: number }[][];
  xLabels?: string[];
  yLabels?: string[];
  cellHeight?: string;
  square?: boolean;
  xLabelsPos?: "top" | "bottom";
  yLabelsPos?: "left" | "right";
  xLabelsStyle?: (index: number) => {};
  yLabelsStyle?: (index: number) => {};
  cellStyle?: (x: number, y: number, ratio: number, rowIndex: number) => {};
  cellRender?: (
    x: number,
    y: number,
    value: { item: any; present: number }
  ) => {};
  onClick?: (
    x: number,
    y: number,
    value: { item: any; present: number }
  ) => void;
  onHover?: (
    x: number,
    y: number,
    value: { item: any; present: number }
  ) => void;
}

function getMinMax(data: { item: any; present: number }[][]): [number, number] {
  const flatArray = data.reduce((acc, row) => [...acc, ...row], []);
  const max = Math.max(...flatArray.map((obj) => obj.present));
  const min = Math.min(...flatArray.map((obj) => obj.present));
  return [min, max];
}

const HeatMapGrid = ({
  data,
  xLabels,
  yLabels,
  xLabelsPos = "top",
  yLabelsPos = "left",
  square = false,
  cellHeight = "2px",
  xLabelsStyle,
  yLabelsStyle,
  cellStyle,
  cellRender,
  onClick,
  onHover,
}: HeatMapGridProps) => {
  const [xLabelHeight, xLabelRef] = useElementHeight(22);
  const [min, max] = getMinMax(data);
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const minMaxDiff = max - min;
  const isXLabelReverse = xLabelsPos === "bottom";
  const isYLabelReverse = yLabelsPos === "right";

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Box
      style={{
        width: "100%",
        maxWidth:
          window.innerWidth > 900
            ? "calc(100vw - 340px)"
            : "calc(100vw - 100px)",
        overflowX: "auto",
      }}
    >
      <Row reverse={isYLabelReverse}>
        {yLabels && (
          <YLabelAligner
            xLabelHeight={xLabelHeight}
            isXLabelReverse={isXLabelReverse}
          >
            <YLabels
              reverse={isYLabelReverse}
              labels={yLabels}
              height={cellHeight}
              yLabelsStyle={yLabelsStyle}
            />
          </YLabelAligner>
        )}
        <Column
          reverse={isXLabelReverse}
          grow={!square}
          className="heatmap-graph-values-container"
        >
          <div ref={xLabelRef}>
            {xLabels && (
              <XLabels
                labels={xLabels}
                xLabelsStyle={xLabelsStyle}
                height={cellHeight}
                square={square}
              />
            )}
          </div>
          <Column>
            {data.map((rowItems, xi) => (
              <Row key={xi}>
                {rowItems.map((value, yi) => (
                  <Cell
                    key={`${xi}-${yi}`}
                    posX={xi}
                    posY={yi}
                    onClick={onClick}
                    onHover={onHover}
                    value={value}
                    height={cellHeight}
                    square={square}
                    render={cellRender}
                    style={cellStyle}
                    rowIndex={xi}
                    ratio={(value.present - min) / minMaxDiff}
                  />
                ))}
              </Row>
            ))}
          </Column>
        </Column>
      </Row>
    </Box>
  );
};

export default HeatMapGrid;
